import http from '../client';
import { errorHandler } from '../errorHandler';
import type { ApiResponse } from '../types';
import {
  collectRecipeIngredients,
  collectRecipePreparationSteps,
} from '../shared/recipeTransformers';

export type CreateRecipeInput = {
  cover_image: File;
  cover_video: File | null;
  title: string;
  description?: string;
  dosha_ids: number[];
  season: string[];
  tags: string[];
};

export interface RecipeApproval {
  id: number;
  recipe_id: number;
  reviewer_id: number;
  decision?: 'approved' | 'rejected' | 'changes_requested';
  comments?: string;
  rating?: number;
  status?: string;
  created_at: string;
  createdAt?: string;
  submitted_at?: string;
  reviewer?: {
    id: number;
    name: string;
    email: string;
    role: string;
    can_approve?: boolean;
  };
}

export interface ApproverSummary {
  total_reviews: number;
  weighted_avg: number | null;
  average_rating: number | null;
}

export interface RecipeActivityLog {
  id: number;
  recipe_id: number;
  recipe_version: number;
  actor_id: number;
  actor_role: string;
  action: string;
  reason?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  createdAt?: string;
  actor?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

export interface Ingredient {
  id?: string | number;
  ingredient_id?: string | number;
  ingredient_name?: string;
  name?: string; // Can be derived from ingredient_name if not present
  quantity: string | number;
  unit: string;
  contra_indications?: string;
  contraindications?: string;
  notes?: string;
  // Substitution metadata on the remedy-ingredient relation
  is_substitute?: boolean;
  parent_recipe_ingredient_id?: string | number | null;
  parent_contraindication_id?: string | number | null;
  skip_for_contraindication?: boolean;
  type?: 'ingredient' | 'medicine';
}

// Helper type for components that require name to be present
export type RecipeIngredient = Ingredient & {
  name: string; // Ensures name is always present
};

export interface PreparationStep {
  id?: string | number;
  title: string;
  description: string;
  step?: number;
  step_number?: number; // Alternative name
  instruction?: string; // Alternative name for description
  duration?: number;
  image?: string;
  image_url?: string; // Backend field name for image URL
  video?: string;
  video_url?: string;
  created_at?: string;
  updated_at?: string;
}

// Helper type for components that use step_number and instruction
export type RecipeStep = {
  step_number: number;
  instruction: string;
  duration?: number;
};

export interface Recipe {
  id: number;
  title: string;
  description?: string;
  status:
    | 'draft'
    | 'review_requested'
    | 'under_review'
    | 'approved'
    | 'rejected'
    | 'changes_requested'
    | 'archived'
    | 'pending_approver_review'
    | 'pending_doctor_review';
  author_id?: number;
  cover_image?: string;
  featured_image?: string; // Alternative name for cover_image
  prep_time_minutes?: number;
  prep_time?: number; // Alternative name
  cook_time?: number;
  servings?: number;
  difficulty_level?: string;
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  submitted_at?: string;
  submitted_for_approval_at?: string;
  reviewed_at?: string;
  version?: number;
  author?: {
    id: number;
    name: string;
    full_name?: string; // Alternative name field
    email: string;
    role: string;
  };
  // Approver queue specific fields
  approverAction?: unknown;
  hasActedOnAnyVersion?: boolean;
  latestApproverAction?: unknown;
  hasReviewed?: boolean;
  recommended_diet?: string;
  diet_to_avoid?: string;
}

export interface RecipeDetail extends Recipe {
  ingredients?: Ingredient[];
  preparationSteps?: PreparationStep[];
  approvals?: RecipeApproval[];
  approver_summary?: ApproverSummary;
  approver_reviews?: Array<{
    reviewer_name?: string;
    rating?: number;
    comments?: string | null;
    submitted_at?: string | null;
  }>;
}

export interface PaginatedRecipesResponse {
  data: Recipe[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const assignReviewer = async (
  recipeId: string | number,
  reviewerId: string | number | (string | number)[],
): Promise<ApiResponse<RecipeApproval[]>> => {
  try {
    const payload = Array.isArray(reviewerId)
      ? { reviewerIds: reviewerId }
      : { reviewerId };

    const response = await http.post(
      `/recipes/${recipeId}/assign-reviewer`,
      payload,
    );
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

export const recipeApi = {
  createRecipe: async (
    data: CreateRecipeInput,
  ): Promise<ApiResponse<unknown>> => {
    try {
      const formData = new FormData();
      formData.append('cover_image', data.cover_image);
      if (data.cover_video) {
        formData.append('cover_video', data.cover_video);
      }
      formData.append('title', data.title);
      if (data.description) {
        formData.append('description', data.description);
      }
      formData.append('dosha_ids', JSON.stringify(data.dosha_ids ?? []));
      formData.append('season', JSON.stringify(data.season ?? []));
      formData.append('tags', JSON.stringify(data.tags ?? []));

      const response = await http.post('/admin/recipes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getPendingRecipes: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    author_id?: number | string;
    author_search?: string;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<ApiResponse<PaginatedRecipesResponse>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.author_id)
        queryParams.append('author_id', params.author_id.toString());
      if (params?.author_search)
        queryParams.append('author_search', params.author_search);
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params?.sort_order)
        queryParams.append('sort_order', params.sort_order);

      const response = await http.get(
        `/admin/recipes/pending?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getApprovedRecipes: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    author_id?: number | string;
    author_search?: string;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<ApiResponse<PaginatedRecipesResponse>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.author_id)
        queryParams.append('author_id', params.author_id.toString());
      if (params?.author_search)
        queryParams.append('author_search', params.author_search);
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params?.sort_order)
        queryParams.append('sort_order', params.sort_order);

      const response = await http.get(
        `/admin/recipes/approved?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getRecipeForReview: async (
    recipeId: number | string,
  ): Promise<
    ApiResponse<{
      recipe: RecipeDetail;
      ingredients: Ingredient[];
      preparationSteps: PreparationStep[];
      approvals?: RecipeApproval[];
    }>
  > => {
    try {
      console.log('🌐 API Call: GET /recipes/' + recipeId);
      const response = await http.get(`/recipes/${recipeId}`);
      console.log('📡 Raw HTTP Response:', response);
      console.log('📡 Response.data:', response.data);
      console.log('📡 Response.data.data:', response.data.data);

      const data = response.data.data || response.data;

      // Extract remedy data - handle both nested and flat structures
      let recipeData: RecipeDetail;
      let ingredients: Ingredient[] = [];
      let preparationSteps: PreparationStep[] = [];
      let approvals: RecipeApproval[] = [];

      // Check if data has a nested recipe property
      if (data?.recipe && typeof data.recipe === 'object') {
        // Nested structure: { recipe: {...}, ingredients: [...], preparationSteps: [...] }
        recipeData = data.recipe as RecipeDetail;
        ingredients = data.ingredients || [];
        preparationSteps =
          data.preparationSteps || data.preparation_steps || [];
        approvals = data.approvals || [];
      } else {
        // Flat structure: recipe properties are at the top level
        // Extract recipe properties (exclude ingredients, preparationSteps, approvals)
        const {
          ingredients: dataIngredients,
          preparationSteps: dataSteps,
          preparation_steps,
          approvals: dataApprovals,
          ...recipeProps
        } = data;

        recipeData = recipeProps as RecipeDetail;
        ingredients = dataIngredients || [];
        preparationSteps = dataSteps || preparation_steps || [];
        approvals = dataApprovals || [];
      }

      // Transform ingredients to ensure name is always present
      const transformedIngredients = collectRecipeIngredients(
        recipeData,
        ingredients,
      );

      // Transform preparation steps from ALL preparations
      const transformedSteps = collectRecipePreparationSteps(
        recipeData,
        preparationSteps,
      );

      return {
        success: true,
        data: {
          recipe: recipeData,
          ingredients: transformedIngredients,
          preparationSteps: transformedSteps,
          approvals: approvals,
        },
      };
    } catch (error) {
      console.error('❌ API Error in getRecipeForReview:', error);
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  approveRecipe: async (
    recipeId: number | string,
    comments?: string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      // Ensure UUID is properly encoded in URL
      const id = encodeURIComponent(String(recipeId));
      console.log('🌐 API Call: POST /admin/recipes/' + id + '/approve');
      console.log('📋 Remedy ID (UUID):', recipeId);

      const response = await http.post(`/admin/recipes/${id}/approve`, {
        comments: comments || '',
      });

      console.log('📡 Approve Response:', response);
      console.log('📡 Response.data:', response.data);
      console.log('📡 Response.data.data:', response.data?.data);

      // Handle different response structures
      const data = response.data?.data || response.data || response;

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('❌ API Error in approveRecipe:', error);
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  rejectRecipe: async (
    recipeId: number | string,
    comments: string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      // Ensure UUID is properly encoded in URL
      const id = encodeURIComponent(String(recipeId));
      console.log('🌐 API Call: POST /admin/recipes/' + id + '/reject');
      console.log('📋 Remedy ID (UUID):', recipeId);

      const response = await http.post(`/admin/recipes/${id}/reject`, {
        comments: comments,
      });

      console.log('📡 Reject Response:', response);
      console.log('📡 Response.data:', response.data);
      console.log('📡 Response.data.data:', response.data?.data);

      // Handle different response structures
      const data = response.data?.data || response.data || response;

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('❌ API Error in rejectRecipe:', error);
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateRecipeStatus: async (
    recipeId: number | string,
    status: string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      const response = await http.patch(`/admin/recipes/${recipeId}/status`, {
        status,
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getAllRecipes: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    author_id?: number | string;
    author_search?: string;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<ApiResponse<PaginatedRecipesResponse>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.author_id)
        queryParams.append('author_id', params.author_id.toString());
      if (params?.author_search)
        queryParams.append('author_search', params.author_search);
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params?.sort_order)
        queryParams.append('sort_order', params.sort_order);

      const response = await http.get(
        `/admin/recipes/all?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
  getDashboardStats: async (): Promise<
    ApiResponse<{ reviewsSentToday: number }>
  > => {
    try {
      const response = await http.get('/recipes/admin/stats');
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
  archiveRecipe: async (
    recipeId: number | string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      const response = await http.post(`/recipes/${recipeId}/archive`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  unarchiveRecipe: async (
    recipeId: number | string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      const response = await http.post(`/recipes/${recipeId}/unarchive`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  getRecipeActivityLogs: async (
    recipeId: number | string,
  ): Promise<ApiResponse<RecipeActivityLog[]>> => {
    try {
      const response = await http.get(`/recipes/${recipeId}/activity-logs`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },
  hardDeleteRecipe: async (
    recipeId: number | string,
  ): Promise<ApiResponse<unknown>> => {
    try {
      const response = await http.delete(`/recipes/${recipeId}/hard-delete`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  // New Approver Workflow APIs
  getApproverQueue: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    review_status?: 'pending' | 'reviewed';
    author_id?: number | string;
    author_search?: string;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<ApiResponse<PaginatedRecipesResponse>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.review_status)
        queryParams.append('review_status', params.review_status);
      if (params?.author_id)
        queryParams.append('author_id', params.author_id.toString());
      if (params?.author_search)
        queryParams.append('author_search', params.author_search);
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params?.sort_order)
        queryParams.append('sort_order', params.sort_order);

      const response = await http.get(
        `/approver-workflow/queue?${queryParams.toString()}`,
      );
      // Backend returns { success: true, data: [...], pagination: {...} }
      // axios wraps it, so response.data = { success: true, data: [...], pagination: {...} }
      // Return it as-is so frontend can access response.data.data and response.data.pagination
      console.log('🌐 [getApproverQueue API] Raw response:', response.data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  approveRecipeByApprover: async (
    recipeId: string | number,
    notes?: string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      const id = encodeURIComponent(String(recipeId));
      const response = await http.post(
        `/approver-workflow/recipes/${id}/approve`,
        { notes: notes || undefined },
      );
      // Backend returns { success: true, message: '...', data: recipe }
      // axios wraps it, so response.data = { success: true, message: '...', data: recipe }
      const recipeData = response.data.data || response.data;
      console.log('🌐 [approveRecipeByApprover API] Response:', {
        fullResponse: response.data,
        recipeData,
        status: recipeData?.status,
      });
      return {
        success: true,
        data: recipeData,
        message: response.data?.message,
        assignedCount: response.data?.assignedCount,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  rejectRecipeByApprover: async (
    recipeId: string | number,
    notes: string,
  ): Promise<ApiResponse<Recipe>> => {
    try {
      const id = encodeURIComponent(String(recipeId));
      const response = await http.post(
        `/approver-workflow/recipes/${id}/reject`,
        { notes },
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Doctor Review Queue APIs
  getDoctorReviewQueue: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    review_status?: 'pending' | 'reviewed';
    author_id?: number | string;
    author_search?: string;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
  }): Promise<ApiResponse<PaginatedRecipesResponse>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.review_status)
        queryParams.append('review_status', params.review_status);
      if (params?.author_id)
        queryParams.append('author_id', params.author_id.toString());
      if (params?.author_search)
        queryParams.append('author_search', params.author_search);
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params?.sort_order)
        queryParams.append('sort_order', params.sort_order);

      const response = await http.get(
        `/approver-workflow/doctor/queue?${queryParams.toString()}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
};
